import React from "react";
import { Link } from "react-router-dom";

const SECTIONS = [
  {
    h: "Who we are",
    body: (
      <>
        <p>
          Capexara AI ("we", "us", "our") builds the cognitive substrate for capital projects.
          This Privacy Policy explains how we collect, use, share and protect personal data when you visit{" "}
          <a href="https://capexara.com">capexara.com</a>, request a demo, talk to us, or use our products.
        </p>
        <p>
          We are the data controller for personal data processed through this website.
          Registered office: Unit No 603-604, 6th Floor, Tower B, Bhutani Alphathum, Nepz Post Office, Gautam Buddha Nagar, Noida, UP 201305, India.
          Contact: <a href="mailto:hello@capexara.com">hello@capexara.com</a>.
        </p>
      </>
    ),
  },
  {
    h: "What data we collect",
    body: (
      <ul>
        <li><strong>Identity & contact</strong> — name, work email, company, project type, message — when you submit a form, request a demo, or email us.</li>
        <li><strong>Booking data</strong> — meeting time, time zone, custom-question responses — captured by Calendly when you book a slot.</li>
        <li><strong>Technical data</strong> — IP address, user agent, referer URL, origin, language preferences — captured for fraud prevention and security on form submits.</li>
        <li><strong>Usage data</strong> — pages viewed, duration, click paths, heatmaps, session replays — only when you have given Analytics consent (see our <Link to="/cookies">Cookie Notice</Link>).</li>
        <li><strong>Marketing data</strong> — campaign attribution, retargeting identifiers — only when you have given Marketing consent.</li>
      </ul>
    ),
  },
  {
    h: "Why and how we use it",
    body: (
      <ul>
        <li><strong>To deliver our services</strong> (legitimate interest / contract): respond to demo requests, send confirmations, schedule meetings, evaluate fit.</li>
        <li><strong>To improve the site and products</strong> (legitimate interest / consent): aggregated usage analysis, performance tuning, A/B testing.</li>
        <li><strong>To communicate</strong> (legitimate interest / consent): operational replies, follow-ups, and — only if you opt in — newsletters and event invites.</li>
        <li><strong>To meet legal obligations</strong>: tax, audit, anti-money-laundering, and law-enforcement requests where applicable.</li>
        <li><strong>To protect the platform</strong>: brute-force defense, abuse detection, security monitoring.</li>
      </ul>
    ),
  },
  {
    h: "Who we share it with",
    body: (
      <>
        <p>We never sell personal data. We share it only with vendors that help us run the business, under written agreements that prohibit secondary use:</p>
        <ul>
          <li><strong>Resend</strong> — transactional email (lead acknowledgements + internal alerts).</li>
          <li><strong>Calendly</strong> — meeting scheduling.</li>
          <li><strong>MongoDB Atlas</strong> — managed database.</li>
          <li><strong>Cloud hosting provider</strong> — application hosting &amp; CDN.</li>
          <li><strong>Google Analytics 4 / Microsoft Clarity</strong> — anonymized analytics, only if Analytics consent is given.</li>
          <li><strong>LinkedIn / Meta</strong> — retargeting pixels, only if Marketing consent is given.</li>
        </ul>
        <p>We may disclose information to comply with applicable law, enforce our terms, or protect rights, property, or safety.</p>
      </>
    ),
  },
  {
    h: "International transfers",
    body: <p>Some of our processors are located outside India (e.g., the EU and the United States). When personal data leaves India we rely on the contract we have with the processor and the safeguards they publish (GDPR Standard Contractual Clauses, the EU–US Data Privacy Framework where applicable).</p>,
  },
  {
    h: "How long we keep it",
    body: (
      <ul>
        <li><strong>Lead/contact submissions</strong> — 24 months after last interaction, then deleted or anonymized.</li>
        <li><strong>Calendar bookings</strong> — held by Calendly under their retention policy.</li>
        <li><strong>Analytics & marketing identifiers</strong> — until consent is withdrawn or up to 14 months, whichever is shorter.</li>
        <li><strong>Tax/legal records</strong> — as long as Indian law requires.</li>
      </ul>
    ),
  },
  {
    h: "Your rights",
    body: (
      <>
        <p>Subject to applicable law (India's DPDP Act 2023, EU/UK GDPR, etc.), you have the right to:</p>
        <ul>
          <li>Access the personal data we hold about you.</li>
          <li>Correct it if it's inaccurate or incomplete.</li>
          <li>Erase it, where there's no overriding legal reason to keep it.</li>
          <li>Restrict or object to certain processing.</li>
          <li>Port it to another controller in a structured, machine-readable form.</li>
          <li>Withdraw consent (without affecting the lawfulness of past processing).</li>
          <li>Lodge a complaint with the Data Protection Board of India or your local supervisory authority.</li>
        </ul>
        <p>To exercise any right, email us at <a href="mailto:hello@capexara.com">hello@capexara.com</a> with the subject "DSAR". We respond within 30 days.</p>
      </>
    ),
  },
  {
    h: "Security",
    body: <p>We apply role-based access controls, bcrypt password hashing for admin accounts, brute-force lockout, TLS in transit, encryption at rest on managed databases, and continuous logging. No system is perfectly secure — we'll notify you and the relevant authority within 72 hours of becoming aware of a personal-data breach that affects you.</p>,
  },
  {
    h: "Children",
    body: <p>Capexara AI is built for enterprise procurement, engineering, and project-controls professionals. It is not directed to children under 18 and we do not knowingly collect their data.</p>,
  },
  {
    h: "Changes to this policy",
    body: <p>When we change this policy materially we'll update the date below and, where the change affects you, notify you by email or a prominent banner on the site.</p>,
  },
];

export default function Privacy() {
  return (
    <section className="legal-page" data-testid="privacy-page">
      <div className="container">
        <div className="legal-head">
          <div className="eyebrow"><span>● Privacy</span></div>
          <h1>Privacy Policy</h1>
          <div className="legal-meta mono">Effective 1 February 2026 · Version 1.0</div>
          <p className="legal-lede">
            We treat your data the way we'd want our own treated — collected only when there's a real reason,
            held only as long as it stays useful, and protected with the discipline you'd expect from a platform
            built for capital-project decisions.
          </p>
        </div>

        <div className="legal-body">
          {SECTIONS.map((s, i) => (
            <section key={i} className="legal-section">
              <h2>{s.h}</h2>
              {s.body}
            </section>
          ))}

          <div className="legal-foot">
            <Link to="/cookies" className="btn btn-ghost">Read the Cookie Notice <span className="arrow">→</span></Link>
            <Link to="/contact" className="btn btn-primary">Talk to us <span className="arrow">→</span></Link>
          </div>
        </div>
      </div>
    </section>
  );
}
